import ColorInit from "../helpers/ColorInit";
import Header from "../components/Header/Header";
import Banner from "../layout/Banner/Banner";
import FeatureOne from "../layout/Feature/FeatureOne";
import Footer from "../components/Footer/Footer";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { BrandsScroll } from "../components/HomePageBrands/BrandsScroll";

// Import our new unified product component
import { UnifiedProducts } from "../components/Products";
import { useProducts, type Product } from "../hooks/products";
import { addProductToCart } from "../hooks/useSupabase";
import { WishlistProvider } from "../hooks/useWishlistContext";
import Promotions from "../components/homePageProduct/Promotions";
import TrustFactors from "../components/homePageProduct/TrustFactors";
// import Newsletter from "../components/homePageProduct/Newsletter";
import FlashDeals from "../components/homePageProduct/FlashDeals";
import { useFlashDeals } from "../hooks/useFlashDeals";

const HomePage = () => {
  const { data: flashDeals = [] } = useFlashDeals();

  // Fetch Store Front products
  const { products: storeFrontProducts } = useProducts({
    filters: { storeFront: true },
    pageSize: 11, // Fetch enough to fill all slots (5 + 3 + 1 + 2 = 11 needed)
  });

  // Distribute Store Front products
  const bannerProducts = storeFrontProducts.slice(0, 5); // 5 products for Banner Variant One
  const promotionOneProducts = storeFrontProducts.slice(5, 8); // 3 products
  const promotionTwoProducts = storeFrontProducts.slice(8, 9); // 1 product
  const promotionThreeProducts = storeFrontProducts.slice(9, 11); // 2 products

  const handleAddToCart = async (product: Product) => {
    await addProductToCart({ productId: product.id, quantity: 1 });
  };

  const handleQuickView = (_product: Product) => {};

  const handleViewAll = (_type: string) => {};

  return (
    <>
      {/* Custom ScrollToTop Button */}
      <ScrollToTopButton />

      {/* ColorInit */}
      <ColorInit color={false} />

      {/* HeaderOne */}
      <Header />

      {/* BannerOne - Dynamic if data exists, else falls back to default inside component */}
      <Banner variant="one" products={bannerProducts} />

      {/* Brands Infinite Scroll Section */}
      <BrandsScroll />

      {/* Main Categories Feature Section */}
      <FeatureOne />

      {/* Wrap product sections with WishlistProvider */}
      <WishlistProvider>
        {/* Flash Deals Section - Above Featured Products */}
        <FlashDeals
          deals={flashDeals}
          onAddToCart={(p) =>
            addProductToCart({ productId: p.id, quantity: 1 })
          }
        />

        {/* Featured Products Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <UnifiedProducts
            sortType="featured"
            limitCount={8}
            title="Featured Products"
            subtitle="Our most Featured products"
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("featured")}
          />
        </div>
        {promotionOneProducts.length > 0 && (
          <div className="py-8 bg-primary">
            <Promotions variant="one" products={promotionOneProducts} />
          </div>
        )}

        {/* Recent Products Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <UnifiedProducts
            sortType="newest"
            limitCount={8}
            title="Latest Products"
            subtitle="Check out our newest arrivals"
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("recent")}
          />
        </div>
        {promotionTwoProducts.length > 0 && (
          <div className="py-8 px-4 bg-primary">
            <Promotions variant="two" products={promotionTwoProducts} />
          </div>
        )}

        {/* On Sale Products Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <UnifiedProducts
            sortType="on_sale"
            limitCount={8}
            title="On Sale Now"
            subtitle="Don't miss these amazing deals"
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("on_sale")}
          />
        </div>

        {promotionThreeProducts.length > 0 && (
          <div className="py-8 bg-primary">
            <Promotions variant="three" products={promotionThreeProducts} />
          </div>
        )}

        {/* Popular Products Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <UnifiedProducts
            sortType="popular"
            limitCount={8}
            title="Best Sellers"
            subtitle="Our most popular products"
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("popular")}
          />
        </div>
      </WishlistProvider>

      <TrustFactors />

      {/* <Newsletter /> */}

      <Footer />
    </>
  );
};

export default HomePage;
